import type { AddressCandidate } from "./addressCandidate";
import {
    normalizeFromGeocoderResult,
    normalizeFromManualInput,
    normalizeFromPlace,
    normalizeFromPlacePrediction,
} from "./addressCandidate";
import type { GooglePlaceAddressFields } from "./googleAddressParts";
import { extractPlaceDisplayName } from "./googleAddressParts";
import { mapAddressCandidateToFormFields } from "./mapAddressCandidate";
import { shouldAutoFillLocationName, type GoogleAddressComponent } from "./googleAddressParts";

export type LocationSelectionSource =
    | "search"
    | "map-click"
    | "marker-drag"
    | "poi-click"
    | "reverse-geocode";

export interface LocationSelectionResult {
    source: LocationSelectionSource;
    latitude: number;
    longitude: number;
    address: GooglePlaceAddressFields;
    searchDisplayValue: string;
    suggestedLocationName: string | null;
}

export interface LocationFormSelectionSlice {
    name: string;
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
    town: string;
    county: string;
    postcode: string;
    country: string;
    latitude: number | null;
    longitude: number | null;
}

export interface LocationSelectionPinCoordinates {
    latitude: number;
    longitude: number;
}

/** Visible text for the Places search input. */
export function buildSearchDisplayValue(params: {
    formattedAddress?: string | null;
    displayName?: string | null;
    address: GooglePlaceAddressFields;
}): string {
    const formatted = params.formattedAddress?.trim();
    if (formatted) return formatted;

    const displayName = params.displayName?.trim();
    const parts = [
        displayName,
        params.address.addressLine1,
        params.address.addressLine2,
        params.address.addressLine3,
        params.address.town,
        params.address.postcode,
    ].filter((p) => p && p.trim().length > 0);

    const joined = parts.join(", ");
    if (joined) return joined;

    return displayName ?? "";
}

export function locationSelectionFromCandidate(candidate: AddressCandidate): LocationSelectionResult {
    const { address, suggestedLocationName } = mapAddressCandidateToFormFields(candidate);

    return {
        source: candidate.source,
        latitude: candidate.latitude,
        longitude: candidate.longitude,
        address,
        suggestedLocationName,
        searchDisplayValue: buildSearchDisplayValue({
            formattedAddress: candidate.formattedAddress ?? candidate.predictionDescription,
            displayName: candidate.displayName,
            address,
        }),
    };
}

export function locationSelectionFromPlaceDetails(params: {
    source: LocationSelectionSource;
    displayName: string | google.maps.places.Place["displayName"] | null | undefined;
    formattedAddress: string | null | undefined;
    addressComponents: GoogleAddressComponent[];
    latitude: number;
    longitude: number;
}): LocationSelectionResult {
    const candidate = normalizeFromManualInput({
        source: params.source,
        displayName: extractPlaceDisplayName(params.displayName) || null,
        formattedAddress: params.formattedAddress,
        addressComponents: params.addressComponents,
        latitude: params.latitude,
        longitude: params.longitude,
    });

    return locationSelectionFromCandidate(candidate);
}

export function locationSelectionFromGeocoderResult(params: {
    source: LocationSelectionSource;
    latitude: number;
    longitude: number;
    addressComponents: GoogleAddressComponent[];
    formattedAddress?: string | null;
}): LocationSelectionResult {
    const candidate = normalizeFromManualInput({
        source: params.source,
        displayName: null,
        formattedAddress: params.formattedAddress ?? null,
        addressComponents: params.addressComponents,
        latitude: params.latitude,
        longitude: params.longitude,
    });

    return locationSelectionFromCandidate(candidate);
}

export function locationSelectionFromGeocoderResultRaw(params: {
    source: LocationSelectionSource;
    result: google.maps.GeocoderResult;
    pinCoords: LocationSelectionPinCoordinates;
}): LocationSelectionResult {
    const candidate = normalizeFromGeocoderResult({
        source: params.source,
        result: params.result,
        pinCoords: params.pinCoords,
    });

    return locationSelectionFromCandidate(candidate);
}

/** Build a selection from a Places API `Place` after `fetchFields`. */
export function locationSelectionFromGooglePlace(
    source: LocationSelectionSource,
    place: google.maps.places.Place,
    pinOrFormattedAddress?: LocationSelectionPinCoordinates | string | null
): LocationSelectionResult | null {
    const pin =
        pinOrFormattedAddress != null && typeof pinOrFormattedAddress === "object"
            ? pinOrFormattedAddress
            : null;
    const formattedAddressOverride =
        typeof pinOrFormattedAddress === "string" ? pinOrFormattedAddress : null;

    const candidate = normalizeFromPlace({
        source,
        place,
        pinCoords: pin,
        formattedAddressOverride,
    });

    if (!candidate) return null;

    return locationSelectionFromCandidate(candidate);
}

export function locationSelectionFromPlacePrediction(params: {
    place: google.maps.places.Place;
    placePrediction: google.maps.places.PlacePrediction;
}): LocationSelectionResult | null {
    const candidate = normalizeFromPlacePrediction(params);
    if (!candidate) return null;
    return locationSelectionFromCandidate(candidate);
}

export function mergeLocationSelectionIntoFormValues(
    prev: LocationFormSelectionSlice,
    selection: LocationSelectionResult,
    options: {
        nameEditedByUser: boolean;
        nameWasAutoFilled: boolean;
        hasPersistedRecord: boolean;
    }
): { values: LocationFormSelectionSlice; nameWasAutoFilled: boolean } {
    const next: LocationFormSelectionSlice = {
        ...prev,
        addressLine1: selection.address.addressLine1,
        addressLine2: selection.address.addressLine2,
        addressLine3: selection.address.addressLine3,
        town: selection.address.town,
        county: selection.address.county,
        postcode: selection.address.postcode,
        country: selection.address.country,
        latitude: selection.latitude,
        longitude: selection.longitude,
    };

    let nameWasAutoFilled = options.nameWasAutoFilled;

    if (
        selection.suggestedLocationName &&
        shouldAutoFillLocationName({
            currentName: prev.name,
            nameEditedByUser: options.nameEditedByUser,
            nameWasAutoFilled: options.nameWasAutoFilled,
            hasPersistedRecord: options.hasPersistedRecord,
        })
    ) {
        next.name = selection.suggestedLocationName;
        nameWasAutoFilled = true;
    }

    return { values: next, nameWasAutoFilled };
}

export { normalizeFromPlacePrediction, normalizeFromGeocoderResult, normalizeFromPlace };
